import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Brain, Search, Shield, MapPin, BarChart3, Zap, Eye, Layers, Target, TrendingUp, MessageSquare, Filter, AlertTriangle, GitBranch, Calculator, Play, Pause, Maximize2, X } from 'lucide-react';

/* ── Scroll reveal hook ── */
const useReveal = (opts = {}) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { setVisible(true); return; }
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.unobserve(el); } },
      { threshold: opts.threshold || 0.12 }
    );
    obs.observe(el);
    return () => obs.unobserve(el);
  }, [opts.threshold]);
  return [ref, visible];
};

const Reveal = ({ children, className = '', delay = 0 }) => {
  const [ref, vis] = useReveal();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${vis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

/* ── Pipeline step data ── */
const pipelineSteps = [
  {
    step: '01', icon: MessageSquare, color: 'from-violet-500 to-purple-600',
    title: 'Intent Parsing',
    engine: 'Groq LLM (Llama 3.3 70B) + Regex',
    description: 'Hybrid approach — regex catches BHK and currency with 100% accuracy. LLM handles complex intent classification like understanding "show me more options" vs "show 3 BHK".',
    detail: 'Extracts: locality, BHK type, budget range, amenity filters, and intent classification from natural language.'
  },
  {
    step: '02', icon: AlertTriangle, color: 'from-red-500 to-rose-600',
    title: 'Constraint Resolution',
    engine: 'Rule-based conflict detector',
    description: 'Checks for contradictions before processing. "Quiet area near airport" triggers a conflict. Budget below market floor triggers a warning.',
    detail: 'If conflict found, pipeline halts with explanation and suggestion — preventing wasted computation.'
  },
  {
    step: '03', icon: Filter, color: 'from-blue-500 to-cyan-600',
    title: 'Cumulative Filter Merge',
    engine: 'filterState.ts',
    description: 'Merges new filters with existing session. Filters accumulate across conversation turns — never overwritten.',
    detail: 'Turn 1: {locality: "new town", bhk: 2} → Turn 2: + {price_max: 80L} → Turn 3: + {amenity: "school"}'
  },
  {
    step: '04', icon: Search, color: 'from-teal-500 to-emerald-600',
    title: 'Apply Filters',
    engine: 'filterEngine.ts',
    description: '881 properties filtered down to matching results using sequential AND operations. Auto-relaxation if results are too few.',
    detail: 'If results < 3: drops amenity filters → increases budget 15% → drops budget entirely. Always explains what was relaxed.'
  },
  {
    step: '05', icon: Shield, color: 'from-orange-500 to-amber-600',
    title: 'Hazard Mask',
    engine: 'hazardMask.ts + Overpass API',
    description: 'Queries OpenStreetMap for flood-prone zones, noise zones, and waterlogging hotspots. Properties in hazard zones are excluded.',
    detail: 'Each excluded property gets an explanation: "Excluded: within 3km of flood-prone area"'
  },
  {
    step: '06', icon: GitBranch, color: 'from-indigo-500 to-blue-600',
    title: 'Pareto Optimization',
    engine: 'NSGA-II Algorithm',
    description: 'Multi-objective optimization across price, commute time, and amenity density. Finds properties where no other is better on ALL criteria.',
    detail: 'Non-dominated sorting with crowding distance for diversity preservation in the solution set.'
  },
  {
    step: '07', icon: Brain, color: 'from-pink-500 to-rose-600',
    title: 'Weight Prediction',
    engine: 'Bayesian ML + Dirichlet Prior',
    description: 'Learns what the user values most from behavior signals — views, dwell time, shortlists, rejections.',
    detail: 'Cold start matches user profile to 10 buyer personas (IT Commuter, Retired Couple, Budget Family, etc.)'
  },
  {
    step: '08', icon: Target, color: 'from-emerald-500 to-teal-600',
    title: 'MCDM Ranking',
    engine: 'TOPSIS + PROMETHEE II',
    description: 'Two complementary algorithms: TOPSIS provides absolute scores (closeness to ideal). PROMETHEE validates via pairwise comparison.',
    detail: 'When they disagree, the system flags a "ranking discrepancy" so users know the ranking is nuanced.'
  },
  {
    step: '09', icon: Eye, color: 'from-cyan-500 to-sky-600',
    title: 'Explainability',
    engine: 'SHAP Attribution',
    description: 'For each property, explains WHY it ranked where it did. Leave-one-out attribution converted to plain English.',
    detail: '"Urban Vista ranks #1 because: Price 15% below average (+0.18), 2 schools within 1.5km (+0.12)"'
  },
  {
    step: '10', icon: Zap, color: 'from-yellow-500 to-orange-600',
    title: 'Response',
    engine: 'chatFormatter.ts',
    description: 'Formats top 3 results with scores, explanations, hazard warnings, filter relaxation notes, and sensitivity analysis.',
    detail: 'Results appear instantly — ranked list + properties pinned on interactive map.'
  }
];

const differentiators = [
  {
    icon: Brain, title: 'Hybrid Intent Parsing',
    description: 'Regex for precision (BHK, currency values never hallucinated) + LLM for complexity (context-aware intent classification). Regex overrides LLM when both detect a value.'
  },
  {
    icon: Layers, title: 'Dual MCDM Ranking',
    description: 'TOPSIS provides absolute scores. PROMETHEE validates via pairwise comparison. When they disagree, the system flags a "ranking discrepancy" for transparency.'
  },
  {
    icon: Eye, title: 'Explainable Rankings',
    description: 'Every ranked property comes with SHAP-based attribution — users see exactly which criteria drove the ranking. This builds trust and enables informed decisions.'
  },
  {
    icon: TrendingUp, title: 'Adaptive Personalization',
    description: 'Bayesian weight engine learns from user behavior in real-time. If a user keeps viewing expensive properties, the system shifts weight from "price" to "amenity".'
  },
  {
    icon: MapPin, title: 'Live Data Verification',
    description: 'Static property data verified against live OpenStreetMap data. Users can audit actual hospitals, schools, and metro stations — not just developer claims.'
  },
  {
    icon: Shield, title: 'Hazard Screening',
    description: 'Properties in flood zones, noise corridors, and waterlogging hotspots are automatically excluded with explanation. Protects from hidden risks typical portals don\'t surface.'
  }
];


/* ── Active pipeline step component ── */
const PipelineStep = ({ step, index, isActive, onClick }) => {
  const Icon = step.icon;
  return (
    <button
      onClick={() => onClick(index)}
      className={`text-left w-full px-4 py-3.5 rounded-xl border transition-all duration-300 ${
        isActive
          ? 'bg-slate-800/80 border-teal-500/30 shadow-lg shadow-teal-500/5'
          : 'bg-transparent border-slate-800/50 hover:border-slate-700 hover:bg-slate-800/30'
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${step.color} flex items-center justify-center flex-shrink-0 ${isActive ? 'opacity-100' : 'opacity-40'} transition-opacity`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-mono text-slate-500">{step.step}</span>
            <span className={`text-sm font-semibold truncate ${isActive ? 'text-white' : 'text-slate-400'} transition-colors`}>
              {step.title}
            </span>
          </div>
          <span className={`text-[10px] font-mono tracking-wider ${isActive ? 'text-teal-400/60' : 'text-slate-600'} transition-colors`}>
            {step.engine}
          </span>
        </div>
      </div>
    </button>
  );
};

/* ══════════════════════════════════════
   MAIN PAGE
   ══════════════════════════════════════ */
const ProductDetailPage = () => {
  const { productId } = useParams();
  const [pageVisible, setPageVisible] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  // Video state
  const demoVideoUrl = process.env.REACT_APP_PRODUCT_DEMO_URL || 'https://res.cloudinary.com/dvfhiihxr/video/upload/v1771424076/demo-video_tvpzfi.mp4';
  const videoRef = useRef(null);
  const modalVideoRef = useRef(null);
  const controlTimerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showControl, setShowControl] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);

  const handlePlayToggle = () => {
    const v = videoRef.current;
    if (!v) return;
    setShowControl(true);
    v.paused ? v.play() : v.pause();
  };

  const showControlBriefly = () => {
    setShowControl(true);
    if (controlTimerRef.current) clearTimeout(controlTimerRef.current);
    if (isPlaying) {
      controlTimerRef.current = setTimeout(() => setShowControl(false), 2000);
    }
  };

  const handleVideoMouseMove = () => {
    if (!isPlaying) return;
    showControlBriefly();
  };

  useEffect(() => {
    if (controlTimerRef.current) clearTimeout(controlTimerRef.current);
    if (isPlaying) {
      controlTimerRef.current = setTimeout(() => setShowControl(false), 2000);
    } else {
      setShowControl(true);
    }
  }, [isPlaying]);

  useEffect(() => {
    if (fullscreen) {
      document.body.style.overflow = 'hidden';
      const inlineVideo = videoRef.current;
      if (inlineVideo && !inlineVideo.paused) inlineVideo.pause();
    } else {
      document.body.style.overflow = '';
      const modalVideo = modalVideoRef.current;
      if (modalVideo && !modalVideo.paused) modalVideo.pause();
    }
    return () => { document.body.style.overflow = ''; };
  }, [fullscreen]);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape' && fullscreen) setFullscreen(false); };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [fullscreen]);

  useEffect(() => {
    return () => {
      if (controlTimerRef.current) clearTimeout(controlTimerRef.current);
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Property Intelligence Platform — Insust Global';
    const t = setTimeout(() => setPageVisible(true), 50);
    return () => clearTimeout(t);
  }, [productId]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || fullscreen) return undefined;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const playPromise = v.play();
          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => {});
          }
        } else if (!v.paused) {
          v.pause();
        }
      },
      { threshold: 0.5 }
    );

    obs.observe(v);
    return () => obs.disconnect();
  }, [fullscreen]);

  if (productId !== 'property-intelligence') {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Product Not Found</h1>
          <Link to="/" className="text-teal-400 hover:text-teal-300">&larr; Back to Home</Link>
        </div>
      </div>
    );
  }

  const currentStep = pipelineSteps[activeStep];

  return (
    <div className={`min-h-screen bg-slate-950 transition-opacity duration-700 ${pageVisible ? 'opacity-100' : 'opacity-0'}`}>

      {/* ── Nav ── */}
      <nav className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 lg:px-12 h-16 bg-slate-950/90 backdrop-blur-lg border-b border-slate-800/50">
        <Link to="/" className="flex items-center gap-3">
          <img src="/logo.jpeg" alt="Insust Global logo" className="h-8 w-8 rounded-full object-contain" />
          <span className="text-lg font-bold text-white">Insust Global</span>
        </Link>
        <Link to="/" className="flex items-center gap-2 text-sm text-slate-400 hover:text-teal-400 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>
      </nav>

      {/* ── Hero ── */}
      <section className="min-h-[85vh] flex items-center pt-16 relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0">
          <div className="absolute top-[-20%] right-[-10%] w-[50vw] h-[50vw] rounded-full bg-teal-600/[0.04] blur-[120px]" />
          <div className="absolute bottom-[-20%] left-[-10%] w-[40vw] h-[40vw] rounded-full bg-cyan-500/[0.03] blur-[100px]" />
          <div className="absolute inset-0 opacity-[0.025]" style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px)',
            backgroundSize: '60px 60px'
          }} />
        </div>

        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Reveal>
                <span className="inline-flex items-center gap-3 px-4 py-2 rounded-full border border-teal-500/20 bg-teal-500/5 mb-6">
                  <span className="w-2 h-2 rounded-full bg-teal-400 animate-pulse" />
                  <span className="text-xs font-mono tracking-[0.15em] uppercase text-teal-400">Live Product</span>
                </span>
              </Reveal>

              <Reveal delay={100}>
                <h1 className="text-4xl lg:text-6xl font-bold text-white mb-4 leading-[1.05] tracking-tight">
                  Property<br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-300 via-cyan-300 to-teal-400">
                    Intelligence
                  </span>
                  <br />Platform
                </h1>
              </Reveal>

              <Reveal delay={200}>
                <p className="text-lg text-slate-400 leading-relaxed max-w-lg mb-8">
                  AI-powered real estate investment advisor. Search, analyze, rank, and audit
                  property investments through <strong className="text-slate-200">natural conversation</strong> — powered by
                  multi-criteria decision mathematics.
                </p>
              </Reveal>

              <Reveal delay={300}>
                <div className="flex flex-wrap gap-3">
                  <a href="#how-it-works" className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 hover:bg-teal-500 text-white font-semibold text-sm uppercase tracking-wider transition-all">
                    See How It Works <ArrowRight className="w-4 h-4" />
                  </a>
                  <a href="#differentiators" className="inline-flex items-center gap-2 px-6 py-3 border border-slate-600 text-white hover:border-teal-400 hover:text-teal-400 font-semibold text-sm uppercase tracking-wider transition-all">
                    Why It's Different
                  </a>
                </div>
              </Reveal>
            </div>

            {/* Product Demo Video */}
            <Reveal delay={400}>
              <div className="overflow-hidden rounded-2xl bg-slate-900/60 border border-slate-800/90 p-3 backdrop-blur-sm">
                <div className="flex items-center gap-2 px-2 pb-3">
                  <span className="w-2.5 h-2.5 rounded-full bg-slate-600" />
                  <span className="w-2.5 h-2.5 rounded-full bg-slate-600" />
                  <span className="w-2.5 h-2.5 rounded-full bg-slate-600" />
                  <span className="text-xs text-slate-400 ml-2">Product Demo</span>
                </div>
                <div
                  className="aspect-video bg-slate-950 rounded-xl overflow-hidden relative"
                  onMouseMove={handleVideoMouseMove}
                  onTouchStart={() => showControlBriefly()}
                >
                  <video
                    ref={videoRef}
                    className="w-full h-full object-contain"
                    preload="metadata"
                    playsInline
                    muted
                    loop
                    disablePictureInPicture
                    controlsList="nodownload noplaybackrate noremoteplayback"
                    onContextMenu={(e) => e.preventDefault()}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    onEnded={() => setIsPlaying(false)}
                  >
                    <source src={demoVideoUrl} type="video/mp4" />
                  </video>
                  <button
                    type="button"
                    onClick={handlePlayToggle}
                    className={`absolute inset-0 m-auto w-16 h-16 rounded-full bg-slate-950/70 border border-slate-300/30 text-white flex items-center justify-center transition-all duration-300 hover:bg-slate-950/85 ${
                      showControl || !isPlaying ? 'opacity-100' : 'opacity-0 pointer-events-none'
                    }`}
                    aria-label={isPlaying ? 'Pause' : 'Play'}
                  >
                    {isPlaying ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6 ml-0.5" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => setFullscreen(true)}
                    className={`absolute bottom-3 right-3 w-9 h-9 rounded-lg bg-slate-950/70 border border-slate-300/20 text-white flex items-center justify-center transition-all duration-300 hover:bg-slate-950/90 hover:border-teal-400/50 ${
                      showControl || !isPlaying ? 'opacity-100' : 'opacity-0 pointer-events-none'
                    }`}
                    aria-label="View fullscreen"
                  >
                    <Maximize2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Stats strip ── */}
      <Reveal>
        <section className="bg-slate-900 border-y border-slate-800">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 px-6 lg:px-16 py-10 max-w-[1400px] mx-auto">
            {[
              { value: '881+', label: 'Properties Indexed' },
              { value: '10', label: 'Pipeline Steps' },
              { value: '70%', label: 'Faster Due Diligence' },
              { value: '42', label: 'Automated Tests' },
              { value: '<2s', label: 'Query Response' },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <div className="text-2xl lg:text-3xl font-bold text-teal-400">{s.value}</div>
                <div className="text-[10px] font-mono tracking-wider uppercase text-slate-500 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </section>
      </Reveal>

      {/* ── User Journey ── */}
      <section id="how-it-works" className="py-24 px-6 lg:px-12">
        <div className="max-w-[1400px] mx-auto">
          <Reveal>
            <div className="text-center mb-16">
              <span className="text-xs font-mono tracking-[0.2em] uppercase text-teal-400 mb-3 block">The User Journey</span>
              <h2 className="text-3xl lg:text-5xl font-bold text-white max-w-2xl mx-auto">
                From question to <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-300 to-cyan-400">decision</span>
              </h2>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: MessageSquare, step: '01', title: 'Ask Naturally', desc: 'Type any property query in plain English. The AI extracts intent, budget, location, and preferences.' },
              { icon: Filter, step: '02', title: 'Pipeline Processes', desc: '10-step deterministic pipeline filters, excludes hazards, and ranks using dual MCDM algorithms.' },
              { icon: BarChart3, step: '03', title: 'Ranked Results', desc: 'Properties ranked with scores and SHAP explanations. Pinned on an interactive map.' },
              { icon: Search, step: '04', title: 'Refine & Explore', desc: 'Add filters conversationally — "near good schools" re-ranks with proximity boost. Filters accumulate.' },
              { icon: Calculator, step: '05', title: 'Financial Deep Dive', desc: 'Select a property config — get 5-year appreciation, EMI projections, and 3 ROI scenarios.' },
              { icon: MapPin, step: '06', title: 'Live Verification', desc: '"Show me hospitals nearby" — real-time Overpass API query pins actual POIs with distances.' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <Reveal key={i} delay={i * 80}>
                  <div className="group p-6 rounded-xl border border-slate-800/50 bg-slate-900/30 hover:bg-slate-900/60 hover:border-teal-500/20 transition-all duration-300 h-full">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-lg bg-teal-500/10 border border-teal-500/20 flex items-center justify-center group-hover:bg-teal-500/20 transition-colors">
                        <Icon className="w-5 h-5 text-teal-400" />
                      </div>
                      <span className="text-[10px] font-mono text-slate-600">{item.step}</span>
                    </div>
                    <h3 className="text-base font-bold text-white mb-2">{item.title}</h3>
                    <p className="text-sm text-slate-400 leading-relaxed">{item.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── 10-Step Pipeline (Interactive) ── */}
      <section id="pipeline" className="py-24 px-6 lg:px-12 bg-slate-900/50 border-y border-slate-800/50">
        <div className="max-w-[1400px] mx-auto">
          <Reveal>
            <div className="text-center mb-16">
              <span className="text-xs font-mono tracking-[0.2em] uppercase text-teal-400 mb-3 block">Core IP</span>
              <h2 className="text-3xl lg:text-5xl font-bold text-white max-w-3xl mx-auto mb-4">
                10-Step Deterministic Pipeline
              </h2>
              <p className="text-base text-slate-400 max-w-2xl mx-auto">
                Every search passes through this pipeline. Click each step to see how your query is processed.
              </p>
            </div>
          </Reveal>

          <div className="grid lg:grid-cols-[340px_1fr] gap-6">
            {/* Step list */}
            <div className="space-y-1.5 max-h-[680px] overflow-y-auto pr-2 custom-scrollbar">
              {pipelineSteps.map((step, i) => (
                <PipelineStep
                  key={i}
                  step={step}
                  index={i}
                  isActive={activeStep === i}
                  onClick={setActiveStep}
                />
              ))}
            </div>

            {/* Detail panel */}
            <div className="relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-950 min-h-[400px]">
              {pipelineSteps.map((step, i) => {
                const Icon = step.icon;
                return (
                  <div
                    key={i}
                    className={`absolute inset-0 p-8 lg:p-12 flex flex-col justify-center transition-all duration-400 ${
                      activeStep === i ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-6 pointer-events-none'
                    }`}
                  >
                    {/* Glow */}
                    <div className={`absolute top-0 right-0 w-64 h-64 rounded-full bg-gradient-to-br ${step.color} opacity-[0.04] blur-[80px]`} />

                    <div className="relative z-10">
                      <div className="flex items-center gap-4 mb-6">
                        <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${step.color} flex items-center justify-center shadow-lg`}>
                          <Icon className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <span className="text-[10px] font-mono text-slate-500 tracking-wider">STEP {step.step} OF 10</span>
                          <h3 className="text-2xl font-bold text-white">{step.title}</h3>
                        </div>
                      </div>

                      <div className="mb-6 px-4 py-2 bg-slate-800/50 rounded-lg border border-slate-700/50 inline-flex items-center gap-2">
                        <Zap className="w-3 h-3 text-teal-400" />
                        <span className="text-xs font-mono text-slate-400">Engine: {step.engine}</span>
                      </div>

                      <p className="text-base text-slate-300 leading-relaxed mb-6 max-w-xl">
                        {step.description}
                      </p>

                      <div className="p-4 rounded-xl bg-slate-900/80 border border-slate-800/80 max-w-xl">
                        <span className="text-[10px] font-mono text-teal-400/60 tracking-wider uppercase block mb-2">Technical Detail</span>
                        <p className="text-sm text-slate-400 leading-relaxed font-mono">
                          {step.detail}
                        </p>
                      </div>

                      {/* Navigation */}
                      <div className="flex items-center gap-3 mt-8">
                        <button
                          onClick={() => setActiveStep(Math.max(0, activeStep - 1))}
                          disabled={activeStep === 0}
                          className="px-4 py-2 border border-slate-700 text-slate-400 hover:text-white hover:border-slate-600 text-xs font-mono uppercase tracking-wider transition-colors disabled:opacity-30 disabled:cursor-not-allowed rounded-lg"
                        >
                          ← Prev
                        </button>
                        <button
                          onClick={() => setActiveStep(Math.min(9, activeStep + 1))}
                          disabled={activeStep === 9}
                          className="px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white text-xs font-mono uppercase tracking-wider transition-colors disabled:opacity-30 disabled:cursor-not-allowed rounded-lg"
                        >
                          Next →
                        </button>
                        <span className="text-[10px] font-mono text-slate-600 ml-auto">
                          {activeStep + 1} / 10
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ── Key Differentiators ── */}
      <section id="differentiators" className="py-24 px-6 lg:px-12">
        <div className="max-w-[1400px] mx-auto">
          <Reveal>
            <div className="mb-16">
              <span className="text-xs font-mono tracking-[0.2em] uppercase text-teal-400 mb-3 block">Why It's Different</span>
              <h2 className="text-3xl lg:text-5xl font-bold text-white max-w-2xl">
                Key Differentiators
              </h2>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {differentiators.map((d, i) => {
              const Icon = d.icon;
              return (
                <Reveal key={i} delay={i * 80}>
                  <div className="group p-7 rounded-xl border border-slate-800/50 hover:border-teal-500/20 bg-slate-900/20 hover:bg-slate-900/40 transition-all duration-300 h-full">
                    <div className="w-11 h-11 rounded-lg bg-teal-500/10 border border-teal-500/20 flex items-center justify-center mb-5 group-hover:bg-teal-500/20 transition-colors">
                      <Icon className="w-5 h-5 text-teal-400" />
                    </div>
                    <h3 className="text-lg font-bold text-white mb-3">{d.title}</h3>
                    <p className="text-sm text-slate-400 leading-relaxed">{d.description}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="bg-teal-700 py-20 px-6 text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.08'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/svg%3E\")",
          }}
        />
        <Reveal>
          <div className="relative z-10 max-w-2xl mx-auto">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Ready to Experience Intelligent Property Search?
            </h2>
            <p className="text-base text-teal-100/70 mb-8 leading-relaxed">
              See how the Property Intelligence Platform transforms your investment decisions with AI-powered spatial analysis.
            </p>
            <div className="flex gap-3 justify-center flex-wrap">
              <Link to="/" className="px-8 py-3 bg-white hover:bg-slate-100 text-teal-700 font-bold text-sm uppercase tracking-wider transition-colors">
                Request Demo
              </Link>
              <a href="#pipeline" className="px-8 py-3 border-2 border-white/50 hover:border-white text-white font-bold text-sm uppercase tracking-wider transition-colors">
                Explore Pipeline
              </a>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ── Video Fullscreen Modal ── */}
      {fullscreen && (
        <div
          className="fixed inset-0 z-[60] bg-slate-950/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
          onClick={() => setFullscreen(false)}
        >
          <button
            onClick={() => setFullscreen(false)}
            className="absolute top-6 right-6 w-10 h-10 rounded-full bg-slate-800 border border-slate-600 text-white flex items-center justify-center hover:bg-slate-700 transition-colors z-10"
            aria-label="Close video"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <div className="overflow-hidden rounded-2xl border border-slate-700 bg-slate-900 shadow-2xl">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-800">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
                <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/80" />
                <span className="w-2.5 h-2.5 rounded-full bg-green-500/80" />
                <span className="text-xs text-slate-400 ml-2">Product Demo — Insust Global Property Intelligence Platform</span>
              </div>
              <div className="aspect-video bg-slate-950">
                <video
                  ref={modalVideoRef}
                  className="w-full h-full object-contain"
                  autoPlay
                  controls
                  playsInline
                  controlsList="nodownload noplaybackrate noremoteplayback"
                  onContextMenu={(e) => e.preventDefault()}
                >
                  <source src={demoVideoUrl} type="video/mp4" />
                </video>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Footer ── */}
      <footer className="bg-slate-950 border-t border-slate-800 py-6 px-6 lg:px-16 flex flex-col sm:flex-row items-center justify-between gap-4">
        <span className="text-xs text-slate-600">&copy; {new Date().getFullYear()} Insust Global. All rights reserved.</span>
        <div className="flex gap-4 text-xs text-slate-600">
          <span className="hover:text-teal-400 cursor-pointer transition-colors">Privacy Policy</span>
          <span className="hover:text-teal-400 cursor-pointer transition-colors">Terms of Service</span>
          <Link to="/" className="hover:text-teal-400 transition-colors">Home</Link>
        </div>
      </footer>
    </div>
  );
};

export default ProductDetailPage;
